import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Typeahead, { TypeaheadFetchFunction, TypeaheadOption, TypeaheadParseFunction } from '../../inputs/Typeahead';
import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { useFormikField } from './hooks/useFormikField';
import { BaseFormikFieldProps } from './types/FormikFieldProps';
import { AutocompleteProps, SxProps, Theme } from '@mui/material';
import { AutocompleteAction } from '../../inputs/Autocomplete';

export type FormikTypeaheadProps<T = unknown, TFormData = Record<string, unknown>> = BaseFormikFieldProps<TFormData> & {
  /** Function to fetch data based on search term */
  fetchData: TypeaheadFetchFunction<T>;
  /** Function to parse the display text after selection (supports regex) */
  parseDisplayText?: TypeaheadParseFunction<T>;
  /** Minimum characters to trigger search */
  minSearchLength?: number;
  /** Whether to show loading state */
  loading?: boolean;
  /** Text to display when loading */
  loadingText?: React.ReactNode;
  /** Text to display when no options are available */
  noOptionsText?: React.ReactNode;
  /** Size of the input */
  size?: 'small' | 'medium';
  /** Styles to apply to the component */
  sx?: SxProps<Theme>;
  /** Custom actions to display at the top of the dropdown */
  actions?: AutocompleteAction[];
  /** Whether to focus the input on mount */
  autoFocus?: boolean;
  /** Reference to the input element */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Placeholder text to display in the input */
  placeholder?: string;
  /** Whether to clear the input on blur */
  clearOnBlur?: boolean;
  /** Whether to clear the input on escape */
  clearOnEscape?: boolean;
  /** Whether to disable the clear button */
  disableClearable?: boolean;
  /** Whether to disable closing on select */
  disableCloseOnSelect?: boolean;
  /** Function to filter options */
  filterOptions?: (options: TypeaheadOption<T>[], state: { inputValue: string }) => TypeaheadOption<T>[];
  /** Function to get the label for an option */
  getOptionLabel?: (option: TypeaheadOption<T>) => string;
  /** Function to check if an option is equal to a value */
  isOptionEqualToValue?: (option: TypeaheadOption<T>, value: TypeaheadOption<T>) => boolean;
  /** Function to render an option */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: TypeaheadOption<T>) => React.ReactNode;
  /** Callback fired when the value changes */
  onChange?: (value: TypeaheadOption<T> | null) => void;
  /** Initial value with both ID and label */
  initialOption?: TypeaheadOption<T>;
  /** Current value */
  value?: TypeaheadOption<T> | null;
  /** Whether to load initial data on mount */
  loadInitialData?: boolean;
  /** Props to be passed to the internal MUI components */
  slotProps?: AutocompleteProps<TypeaheadOption<T>, false, false, false>['slotProps'];
  /** Test ID for testing (pass string e.g. testId="page:tab:element") */
  testId?: string;
  /** @deprecated Use testId instead */
  'data-testid'?: string;
};

/**
 * Typeahead component integrated with Formik
 *
 * @example
 * ```tsx
 * <FormikTypeahead<TypeaheadOption, FormValues>
 *   id="user"
 *   label="Search Users"
 *   fetchData={async (searchTerm) => {
 *     const response = await fetch(`/api/users?search=${searchTerm}`);
 *     const users = await response.json();
 *     return users.map(user => ({
 *       value: user.id,
 *       label: `${user.firstName} ${user.lastName}`,
 *       displayText: `${user.firstName} ${user.lastName} (${user.email})`,
 *       data: user
 *     }));
 *   }}
 *   parseDisplayText={(option) => {
 *     // Use regex to extract and format the display text
 *     const match = option.displayText?.match(/(.+) \((.+)\)/);
 *     return match ? `${match[1]} - ${match[2]}` : option.label;
 *   }}
 *   actions={[
 *     {
 *       label: 'View All Users',
 *       icon: <ListIcon />,
 *       onClick: () => navigate('/users')
 *     },
 *     {
 *       label: 'Add New User',
 *       icon: <AddIcon />,
 *       onClick: () => navigate('/users/new')
 *     }
 *   ]}
 * />
 * ```
 */
const FormikTypeahead = <T extends unknown, TFormData extends Record<string, unknown>>({
  id,
  label,
  fetchData,
  parseDisplayText,
  minSearchLength = 2,
  loading = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options',
  size = 'medium',
  sx,
  actions,
  autoFocus = false,
  inputRef,
  placeholder,
  clearOnBlur = true,
  clearOnEscape = true,
  disableClearable = true,
  disableCloseOnSelect = false,
  filterOptions,
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  onChange: propOnChange,
  initialOption,
  value,
  loadInitialData = false,
  slotProps: slotPropsProp,
  testId: testIdProp,
  'data-testid': dataTestId
}: FormikTypeaheadProps<T, TFormData>) => {
  const { status, setFieldValue, errorText, hasError, fieldValue } = useFormikField<TFormData>({ id });
  const baseTestId = testIdProp ?? dataTestId;
  const initialValueSet = useRef(false);
  const [selectedOption, setSelectedOption] = useState<TypeaheadOption<T> | null>(null);

  // Reset initialValueSet when fieldValue is cleared/reset
  useEffect(() => {
    if (!fieldValue && initialValueSet.current) {
      initialValueSet.current = false;
      setSelectedOption(null);
    }
  }, [fieldValue, id]);

  // Set initial value if provided and not already set
  useEffect(() => {
    if (initialOption && !fieldValue && !initialValueSet.current) {
      // Validate that initialOption has required properties
      if (!initialOption.value || !initialOption.label) {
        console.warn(`FormikTypeahead: Invalid initialOption provided for field "${id}"`);
        return;
      }
      initialValueSet.current = true;
      setFieldValue(id as keyof TFormData & string, initialOption.value);
      setSelectedOption(initialOption);
    } else if (initialOption && fieldValue === initialOption.value && !selectedOption) {
      // If the field value matches the initial option but selectedOption is not set, set it
      setSelectedOption(initialOption);
    }
  }, [initialOption, fieldValue, setFieldValue, id, selectedOption]);

  // Find the current option based on fieldValue
  const currentOption = useMemo(() => {
    if (!fieldValue) return null;

    // If we have a selected option that matches the fieldValue, use it
    if (selectedOption && selectedOption.value === fieldValue) {
      return selectedOption;
    }

    // If initialOption is provided and matches the fieldValue, use it
    if (initialOption && initialOption.value === fieldValue) {
      return initialOption;
    }

    // For typeahead, we typically don't have a static options list
    // The value is usually set directly when an option is selected
    // If we have a fieldValue but no matching option, return null to avoid showing stale data
    return null;
  }, [fieldValue, selectedOption, initialOption]);

  const handleChange = useCallback(
    (newValue: TypeaheadOption<T> | null) => {
      if (newValue) {
        setFieldValue(id as keyof TFormData & string, newValue.value);
        setSelectedOption(newValue);
        propOnChange?.(newValue);
      } else {
        setFieldValue(id as keyof TFormData & string, null);
        setSelectedOption(null);
        propOnChange?.(null);
      }
    },
    [id, setFieldValue, propOnChange]
  );

  const mergedSlotProps = useMemo(() => {
    if (!baseTestId) return slotPropsProp;
    const existing = (slotPropsProp ?? {}) as Record<string, unknown>;
    const existingTextField = (existing.textField ?? {}) as Record<string, unknown>;
    const existingTextFieldSlotProps = (existingTextField.slotProps ?? {}) as Record<string, Record<string, unknown>>;
    const textFieldSlotProps = {
      inputLabel: { ...existingTextFieldSlotProps.inputLabel, [TID_ATTR]: `${baseTestId}:label` },
      htmlInput: { ...existingTextFieldSlotProps.htmlInput, [TID_ATTR]: `${baseTestId}:input` },
      formHelperText: { ...existingTextFieldSlotProps.formHelperText, [TID_ATTR]: `${baseTestId}:helper-text` }
    };
    return {
      ...existing,
      textField: {
        ...existingTextField,
        slotProps: { ...existingTextFieldSlotProps, ...textFieldSlotProps }
      }
    } as typeof slotPropsProp;
  }, [baseTestId, slotPropsProp]);

  const typeaheadProps = useMemo(
    () => ({
      label,
      helperText: errorText,
      error: hasError,
      fetchData,
      parseDisplayText,
      minSearchLength,
      loading,
      loadingText,
      noOptionsText,
      size,
      sx,
      actions,
      autoFocus,
      inputRef,
      placeholder,
      clearOnBlur,
      clearOnEscape,
      disableClearable,
      disableCloseOnSelect,
      filterOptions,
      getOptionLabel,
      isOptionEqualToValue,
      renderOption,
      value: value || currentOption,
      onChange: handleChange,
      loadInitialData,
      slotProps: mergedSlotProps,
      ...(baseTestId ? { [TID_ATTR]: baseTestId } : {})
    }),
    [
      label,
      errorText,
      hasError,
      fetchData,
      parseDisplayText,
      minSearchLength,
      loading,
      loadingText,
      noOptionsText,
      size,
      sx,
      actions,
      autoFocus,
      inputRef,
      placeholder,
      clearOnBlur,
      clearOnEscape,
      disableClearable,
      disableCloseOnSelect,
      filterOptions,
      getOptionLabel,
      isOptionEqualToValue,
      renderOption,
      value,
      currentOption,
      handleChange,
      loadInitialData,
      mergedSlotProps,
      baseTestId
    ]
  );

  if (status?.loading || loading) {
    return <FormikFieldSkeleton />;
  }

  return <Typeahead<T> {...typeaheadProps} />;
};

export default FormikTypeahead;
